import { useState, useEffect, useCallback } from "react";
import { formatPlAmount } from "../utils/formatPlAmount";
import {
  getExpenseBootstrapData,
  getExpenseBootstrapError,
  getExpenseBootstrapPromise,
} from "../state/expenseBootstrapStore";
import { useExpenseBootstrap } from "./useExpenseBootstrap";
import type { KeyMetricsExpenseEntity } from "../../domain/entities/KeyMetricsExpenseEntity";

function applyKeyMetricsEntity(entity: KeyMetricsExpenseEntity): {
  ytdExpense: string;
  ytdSubtext: string;
  dpoAvgDays: number | null;
  dpoChangeText: string | undefined;
  dpoChangeVariant: "up" | "down" | undefined;
  dpoWindowLabel: string;
  gaugeValue: string;
  gaugeValuePercentage: number | null;
  gaugeValueUnit: string;
  gaugeClosedMonthLabel: string;
  gaugeBadgeLabel: string;
} {
  let dpoChangeText: string | undefined;
  let dpoChangeVariant: "up" | "down" | undefined;
  if (entity.dpoChangeDays === 0) {
    dpoChangeText = "No change";
    dpoChangeVariant = undefined;
  } else if (entity.dpoChangeVariant) {
    const absDays = Math.abs(entity.dpoChangeDays ?? 0);
    const arrow = entity.dpoChangeVariant === "up" ? "↑" : "↓";
    const dayLabel = absDays === 1 ? "day" : "days";
    dpoChangeText = `${arrow} ${absDays} ${dayLabel}`;
    dpoChangeVariant = entity.dpoChangeVariant;
  } else {
    dpoChangeText = undefined;
    dpoChangeVariant = undefined;
  }

  return {
    ytdExpense: formatPlAmount(entity.ytdExpense),
    ytdSubtext: entity.ytdSubtext,
    dpoAvgDays: entity.dpoAvgDays != null ? entity.dpoAvgDays : null,
    dpoChangeText,
    dpoChangeVariant,
    dpoWindowLabel: entity.dpoWindowLabel ?? "Latest closed month",
    gaugeValue: entity.gaugeValue != null ? `${entity.gaugeValue}%` : "—",
    gaugeValuePercentage: entity.gaugeValue ?? null,
    gaugeValueUnit: entity.gaugeValueUnit || "EXP ÷ REV",
    gaugeClosedMonthLabel: entity.gaugeClosedMonthLabel?.trim()
      ? entity.gaugeClosedMonthLabel
      : "—",
    gaugeBadgeLabel: entity.gaugeBadgeLabel,
  };
}

export function useKeyMetricsExpense() {
  useExpenseBootstrap();

  const [ytdExpense, setYtdExpense] = useState<string>("—");
  const [ytdSubtext, setYtdSubtext] = useState<string>("");
  const [dpoAvgDays, setDpoAvgDays] = useState<number | null>(null);
  const [dpoChangeText, setDpoChangeText] = useState<string | undefined>(
    undefined,
  );
  const [dpoChangeVariant, setDpoChangeVariant] = useState<
    "up" | "down" | undefined
  >(undefined);
  const [dpoWindowLabel, setDpoWindowLabel] = useState<string>(
    "Latest closed month",
  );
  const [gaugeValue, setGaugeValue] = useState<string>("—");
  const [gaugeValuePercentage, setGaugeValuePercentage] =
    useState<number | null>(null);
  const [gaugeValueUnit, setGaugeValueUnit] =
    useState<string>("EXP ÷ REV");
  const [gaugeClosedMonthLabel, setGaugeClosedMonthLabel] =
    useState<string>("—");
  const [gaugeBadgeLabel, setGaugeBadgeLabel] =
    useState<string>("Healthy");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const syncFromBootstrap = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getExpenseBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getExpenseBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getExpenseBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Expense bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.keyMetrics;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Key metrics unavailable");
      }

      const vm = applyKeyMetricsEntity(section.data);
      setYtdExpense(vm.ytdExpense);
      setYtdSubtext(vm.ytdSubtext);
      setDpoAvgDays(vm.dpoAvgDays);
      setDpoChangeText(vm.dpoChangeText);
      setDpoChangeVariant(vm.dpoChangeVariant);
      setDpoWindowLabel(vm.dpoWindowLabel);
      setGaugeValue(vm.gaugeValue);
      setGaugeValuePercentage(vm.gaugeValuePercentage);
      setGaugeValueUnit(vm.gaugeValueUnit);
      setGaugeClosedMonthLabel(vm.gaugeClosedMonthLabel);
      setGaugeBadgeLabel(vm.gaugeBadgeLabel);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load expense key metrics";
      setError(message);
      setYtdExpense("—");
      setYtdSubtext("");
      setDpoAvgDays(null);
      setDpoChangeText(undefined);
      setDpoChangeVariant(undefined);
      setDpoWindowLabel("Latest closed month");
      setGaugeValue("—");
      setGaugeValuePercentage(null);
      setGaugeClosedMonthLabel("—");
      setGaugeBadgeLabel("Healthy");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void syncFromBootstrap();
  }, [syncFromBootstrap]);

  return {
    ytdExpense,
    ytdSubtext,
    dpoAvgDays,
    dpoChangeText,
    dpoChangeVariant,
    dpoWindowLabel,
    gaugeValue,
    gaugeValuePercentage,
    gaugeValueUnit,
    gaugeClosedMonthLabel,
    gaugeBadgeLabel,
    gaugeFormulaLabel: "Expenses ÷ Revenue",
    loading,
    error,
    refetch: syncFromBootstrap,
  };
}
